// src/services/contactService.ts
import { db } from '../firebase';
import { Contact } from '../models';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';

// Reference to the "contacts" collection
const contactsCollection = collection(db, 'contacts');

/**
 * Fetches all contacts from the Firestore "contacts" collection.
 * Each returned contact includes the document id as the `id` field.
 */
export const getContacts = async (): Promise<Contact[]> => {
  const snapshot = await getDocs(contactsCollection);
  return snapshot.docs.map((docSnapshot) => {
    const data = docSnapshot.data();

    // Convert the interactions array if it exists
    const interactions = Array.isArray(data.interactions)
      ? data.interactions.map((interaction: any) => ({
          ...interaction,
          date:
            interaction.date && typeof interaction.date.toDate === "function"
              ? interaction.date.toDate()
              : new Date(interaction.date.seconds * 1000),
        }))
      : [];

    return {
      ...data,
      id: docSnapshot.id,
      interactions,
    } as Contact;
  });
};


/**
 * Adds a new contact to Firestore.
 * After adding the contact, it updates the document so that the
 * Firestore-generated document id is saved as a field within the document.
 */
export const addContact = async (contact: Omit<Contact, 'id'>): Promise<Contact> => {
  // Add the contact without the id field.
  const docRef = await addDoc(contactsCollection, contact);

  // Update the new document to include the document id as a field.
  await updateDoc(doc(db, 'contacts', docRef.id), { id: docRef.id });

  return { ...contact, id: docRef.id } as Contact;
};

/**
 * Updates an existing contact document in Firestore.
 */
export const updateContact = async (contact: Contact): Promise<void> => {
  if (!contact.id) {
    throw new Error('Contact must have an id to update.');
  }
  const contactDoc = doc(db, 'contacts', contact.id);
  // You can choose to merge fields or overwrite the document.
  await updateDoc(contactDoc, { ...contact });
};
 
  /**
   * Deletes a contact from Firestore.
   */
  export const deleteContact = async (contactId: string): Promise<void> => {
    const contactDocRef = doc(db, 'contacts', contactId);
    await deleteDoc(contactDocRef);
  };